import Phaser from 'phaser';
import { apiBaseUrl, canonicalOrigin } from '../../platform/index.js';
import { getAuth } from '../../store/auth.js';
import { cancelInvite } from '../../store/invites.js';
import { getProfile, onProfileChange } from '../../store/profile.js';
import {
  createRemoteGameStore,
  fetchGameSnapshot,
  type RemoteGameSnapshot,
} from '../../store/RemoteGameStore.js';
import { supabaseBrowser } from '../../store/supabaseClient.js';
import { attachBottomNav, preloadNavAssets } from '../view/AppNav.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { Button } from '../view/Button.js';
import { InviteUsernameModal } from '../view/InviteUsernameModal.js';
import { mountPageHeader } from '../view/PageHeader.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

/**
 * Phase 1 multiplayer lobby. Designed for beta testing with friends:
 *
 *   - On enter: auto-claim a seat via POST /api/games/:id/join
 *   - Show seat list (live-updated via Supabase Realtime)
 *   - Host (seat 0) gets a COPY LINK button so they can share the URL
 *   - Host gets START GAME when 2 humans are seated
 *   - When game.status flips to 'active', launch GameScene with a
 *     RemoteGameStore
 *
 * AI seats (in 2v2-ai games) are pre-inserted server-side and appear
 * in the seat list with an "AI" tag.
 *
 * No name input, color picker, or team config — those are Phase 2
 * lobby polish.
 */
export class JoinScene extends Phaser.Scene {
  static readonly KEY = 'Join';

  private gameId!: string;
  private snapshot: RemoteGameSnapshot | null = null;
  private statusText!: Phaser.GameObjects.Text;
  private seatTexts: Phaser.GameObjects.Text[] = [];
  private hintTexts: Phaser.GameObjects.Text[] = [];
  // Seats card chrome (background + label). Tracked separately from
  // seatTexts so we can destroy + redraw on each render without
  // touching the rest of the scene.
  private seatCardObjects: Phaser.GameObjects.GameObject[] = [];
  private copyButton: Button | null = null;
  private inviteButton: Button | null = null;
  private inviteModal: InviteUsernameModal | null = null;
  private copyFlashText: Phaser.GameObjects.Text | null = null;
  // y-position where the "Link copied!" flash should appear, set by
  // render() to track whichever layout (single vs dual button) is
  // currently shown so the flash lines up with the hint text it
  // momentarily replaces.
  private copyFlashY = 935;
  private channelCleanup?: () => void;
  private transitioning = false;
  // Tracks whether we've already seen one SUBSCRIBED callback so we
  // can tell "initial connection" apart from "reconnected after a
  // drop" — only the latter needs a forced refetch to catch missed
  // events.
  private hasEverSubscribed = false;
  // Profile-gate listener. Set when bootstrap finds the profile is
  // still loading and needs to wait before deciding whether to
  // proceed or route to UsernameSetup. Cleaned up after one event.
  private unsubscribeProfileGate: (() => void) | undefined = undefined;
  // Throttle for visibility-driven refetches. 3s matches the same
  // floor used in RemoteGameStore.
  private lastVisibilityRefetchAt = 0;
  private readonly visibilityHandler = (): void => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - this.lastVisibilityRefetchAt < 3000) return;
    this.lastVisibilityRefetchAt = now;
    void this.refreshSnapshot();
  };

  constructor() {
    super(JoinScene.KEY);
  }

  preload(): void {
    preloadNavAssets(this);
    // Wide horizontal logo — same banner used in the in-game HUD.
    // Distinct key from HomeScene's 'homeLogo' so the two scenes can
    // load different files without preload collisions.
    this.load.image('joinWideLogo', '/assets/logo2.png');
  }

  init(data?: { gameId?: string }): void {
    // Defensive: if started without a gameId (e.g. Phaser auto-start
    // race), log and leave fields empty. create() detects the empty
    // state and shows a friendly message rather than crashing.
    this.gameId = data?.gameId ?? '';
    this.snapshot = null;
    this.seatTexts = [];
    this.hintTexts = [];
    this.seatCardObjects = [];
    this.copyButton = null;
    this.inviteButton = null;
    this.inviteModal = null;
    this.copyFlashText = null;
    this.transitioning = false;
    if (!this.gameId) {
      console.warn(
        'JoinScene started without gameId — staying inert until restarted'
      );
    }
  }

  create(): void {
    // No-op create if started without a gameId — main.ts's deferred
    // restart will re-enter create() with the real gameId.
    if (!this.gameId) return;

    // Backdrop — same deep navy as the rest of the app.
    this.add
      .rectangle(
        DESIGN_WIDTH / 2,
        DESIGN_HEIGHT / 2,
        DESIGN_WIDTH,
        DESIGN_HEIGHT,
        COLORS.bgDeepInt
      )
      .setOrigin(0.5);

    // Shared page header — logo flush at the top, Back overlaid at
    // the top-right. Matches Inbox / Settings / Friends / About /
    // HowTo / GameScene.
    mountPageHeader({
      scene: this,
      logoAssetKey: 'joinWideLogo',
      showBack: true,
    });
    this.drawHeadline();
    this.drawGameCodeCard();
    // JoinScene is a sub-screen of the games section (a specific
    // lobby), so re-tapping the highlighted games icon should route
    // to Inbox instead of being a no-op.
    attachBottomNav(this, 'games', { allowActiveTap: true });

    this.statusText = this.add
      .text(DESIGN_WIDTH / 2, 275, 'Joining game…', {
        fontFamily: FONT_HEADER,
        fontSize: '20px',
        color: COLORS.textSecondary,
        fontStyle: '600',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    // Visibility refresh: if the user backgrounds the tab and comes
    // back, Realtime may have dropped + auto-reconnected silently.
    // Pull a fresh snapshot so the lobby seat list / status reflects
    // anything that happened while we were away.
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.channelCleanup?.();
      this.copyButton?.destroy();
      this.inviteButton?.destroy();
      this.inviteModal?.destroy();
      this.unsubscribeProfileGate?.();
      this.unsubscribeProfileGate = undefined;
    });

    void this.bootstrap();
  }

  // --- Static layout ---

  /**
   * Static "MULTIPLAYER LOBBY" headline. The dynamic state ("Joining
   * game…", "Waiting for 2 more players (2/4)", "Lobby full —
   * starting…") lives in statusText just below this.
   */
  private drawHeadline(): void {
    this.add
      .text(DESIGN_WIDTH / 2, 225, 'MULTIPLAYER LOBBY', {
        fontFamily: FONT_HEADER,
        fontSize: '30px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(5)
      .setOrigin(0.5);
  }

  /**
   * Labeled "GAME CODE" card. Replaces the bare pill so the code
   * sits inside a clear visual container with a label that names
   * what it is. Card chrome matches the rest of the app's
   * bgPanelInt + borderSubtle treatment.
   */
  private drawGameCodeCard(): void {
    const cardW = 420;
    const cardH = 100;
    const cardTop = 325;
    const cardLeft = DESIGN_WIDTH / 2 - cardW / 2;
    const bg = this.add.graphics();
    bg.fillStyle(COLORS.bgPanelInt, 1);
    bg.fillRoundedRect(cardLeft, cardTop, cardW, cardH, 8);
    bg.lineStyle(1, COLORS.borderSubtleInt, 1);
    bg.strokeRoundedRect(cardLeft, cardTop, cardW, cardH, 8);

    this.add
      .text(DESIGN_WIDTH / 2, cardTop + 24, 'GAME CODE', {
        fontFamily: FONT_MONO,
        fontSize: '12px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    this.add
      .text(
        DESIGN_WIDTH / 2,
        cardTop + 64,
        this.gameId.toUpperCase(),
        {
          fontFamily: FONT_MONO,
          fontSize: '28px',
          color: COLORS.accentCyanHex,
          fontStyle: '700',
        }
      )
      .setLetterSpacing(6)
      .setOrigin(0.5);
  }

  // --- Boot flow ---

  private async bootstrap(): Promise<void> {
    // Auth + profile gate before any API call. Routes to AuthScene
    // (with returnToGameId so the user lands back here after
    // signing in) or UsernameSetupScene (same return path) as
    // appropriate. The brief 'loading' state defers the decision
    // until the profile fetch resolves.
    const auth = getAuth();
    if (!auth.user) {
      this.scene.start('Auth', { returnToGameId: this.gameId });
      return;
    }
    const profile = getProfile();
    if (profile.kind === 'missing') {
      this.scene.start('UsernameSetup', { returnToGameId: this.gameId });
      return;
    }
    if (profile.kind === 'unloaded' || profile.kind === 'loading') {
      // Wait one transition then retry bootstrap. The profile
      // module fires the listener once the fetch resolves; we
      // never end up subscribed to multiple gates because each
      // retry runs through this same check and only re-subscribes
      // if still in a waiting state.
      this.statusText.setText('Loading…');
      this.unsubscribeProfileGate?.();
      this.unsubscribeProfileGate = onProfileChange(() => {
        this.unsubscribeProfileGate?.();
        this.unsubscribeProfileGate = undefined;
        void this.bootstrap();
      });
      return;
    }

    try {
      // Step 1: fetch initial snapshot.
      const snap = await fetchGameSnapshot(this.gameId);
      this.snapshot = snap;

      // Already active? Skip the lobby and jump into the game.
      if (snap.status === 'active' && snap.state) {
        this.launchGame();
        return;
      }
      if (snap.status === 'finished') {
        // Hand off to ResultsScene — pass the snapshot through so it
        // doesn't refetch. Reached this way from the email's "View
        // results" CTA and from inbox taps on finished games.
        this.scene.start('Results', { gameId: this.gameId, snapshot: snap });
        return;
      }

      // Step 2: subscribe to Realtime so the seat list updates as
      // other players join. Same channel will deliver the status
      // change to 'active' when the host starts.
      this.subscribeRealtime();

      // Step 3: claim a seat if we haven't already.
      const myId = auth.user.id;
      const alreadySeated = snap.seats.some((s) => s.user_id === myId);
      if (!alreadySeated) {
        await this.joinSeat();
      } else {
        await this.refreshSnapshot();
      }
      this.render();
    } catch (err) {
      // Distinguish auth failure (session expired since the gate
      // check) from "not in this game" from real errors.
      const status = (err as Error & { status?: number }).status;
      if (status === 401) {
        this.scene.start('Auth', { returnToGameId: this.gameId });
        return;
      }
      if (status === 404) {
        this.statusText.setText("This game doesn't exist (or you're not in it).");
        return;
      }
      console.error('JoinScene bootstrap failed', err);
      this.statusText.setText('Could not load game.');
    }
  }

  private async joinSeat(): Promise<void> {
    const token = getAuth().session?.access_token;
    const headers: Record<string, string> = {
      'content-type': 'application/json',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${apiBaseUrl()}/api/games/${this.gameId}/join`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(`join failed: ${JSON.stringify(err)}`);
    }
    await this.refreshSnapshot();
  }

  private async refreshSnapshot(): Promise<void> {
    const snap = await fetchGameSnapshot(this.gameId);
    this.snapshot = snap;
    this.render();
    if (snap.status === 'active' && snap.state) {
      this.launchGame();
    }
  }

  // --- Realtime ---

  private subscribeRealtime(): void {
    const supabase = supabaseBrowser();

    // games row changes — primarily watching for status → 'active'.
    const gameChannel = supabase
      .channel(`join-${this.gameId}`)
      .on(
        'postgres_changes' as never,
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'games',
          filter: `id=eq.${this.gameId}`,
        },
        () => {
          void this.refreshSnapshot();
        }
      )
      .on(
        'postgres_changes' as never,
        {
          event: 'INSERT',
          schema: 'public',
          table: 'game_seats',
          filter: `game_id=eq.${this.gameId}`,
        },
        () => {
          void this.refreshSnapshot();
        }
      )
      // v0.9.8 invites — UPDATE catches held → seated transitions
      // (invitee accepts), DELETE catches host/invitee cancellation.
      // Without these the lobby UI shows stale INVITED rows after a
      // cancel and stale held rows after an accept until something
      // else triggers a refresh.
      .on(
        'postgres_changes' as never,
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'game_seats',
          filter: `game_id=eq.${this.gameId}`,
        },
        () => {
          void this.refreshSnapshot();
        }
      )
      .on(
        'postgres_changes' as never,
        {
          event: 'DELETE',
          schema: 'public',
          table: 'game_seats',
          filter: `game_id=eq.${this.gameId}`,
        },
        () => {
          void this.refreshSnapshot();
        }
      )
      // Status callback mirrors RemoteGameStore: on a reconnect
      // (SUBSCRIBED after we've already seen one), refetch the
      // snapshot to catch lobby changes the dropped channel missed.
      // Errors are logged but otherwise non-fatal — Supabase auto-
      // reconnects on its own.
      .subscribe((status: string, err?: Error) => {
        if (status === 'SUBSCRIBED') {
          if (this.hasEverSubscribed) {
            void this.refreshSnapshot();
          }
          this.hasEverSubscribed = true;
        } else if (
          (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') &&
          err
        ) {
          console.warn(`Lobby realtime ${status} on join-${this.gameId}`, err);
        }
      });

    this.channelCleanup = () => {
      void supabase.removeChannel(gameChannel);
    };
  }

  // --- Render ---

  private render(): void {
    const snap = this.snapshot;
    if (!snap) return;

    const myId = getAuth().user?.id ?? '';
    const seats = snap.seats;
    // Seated humans drive the auto-start gate (must have user_id).
    // Held invite seats (kind='human' AND user_id===null) count
    // against HUMAN_CAP but NOT against target_seats — same rule
    // the server uses. Without this filter the lobby reads "full"
    // the moment the host sends an invite, before the invitee has
    // actually accepted.
    const seatedHumans = seats.filter(
      (s) => s.kind === 'human' && s.user_id != null
    );
    const heldInvites = seats.filter(
      (s) => s.kind === 'human' && s.user_id == null
    );
    const isHost = seats[0]?.user_id === myId;
    const targetSeats = snap.targetSeats;
    const remaining = Math.max(0, targetSeats - seatedHumans.length);
    const canInviteMore = remaining > 0;

    // Status line.
    if (snap.status === 'lobby') {
      const count = `${seatedHumans.length}/${targetSeats}`;
      if (remaining > 0) {
        const noun = remaining === 1 ? 'player' : 'players';
        if (heldInvites.length > 0) {
          // Pending invites — surface that the wait is partly on
          // someone accepting, not just "anyone could join."
          const invitesNoun = heldInvites.length === 1 ? 'invite' : 'invites';
          this.statusText.setText(
            `Waiting for ${heldInvites.length} ${invitesNoun} to accept (${count})`
          );
        } else {
          this.statusText.setText(
            `Waiting for ${remaining} more ${noun} (${count})`
          );
        }
      } else {
        // Filled — the auto-start has been triggered server-side and
        // realtime will deliver the active state in a moment.
        this.statusText.setText(`Lobby full — starting… (${count})`);
      }
    } else if (snap.status === 'active') {
      this.statusText.setText('Starting game…');
    }

    // Seat rows wrapped in a "PLAYERS" card. Rebuilt from scratch
    // every render — the list is small and re-mounting is cheaper
    // than tracking per-row mutations.
    for (const t of this.seatTexts) t.destroy();
    this.seatTexts = [];
    for (const t of this.hintTexts) t.destroy();
    this.hintTexts = [];
    for (const o of this.seatCardObjects) o.destroy();
    this.seatCardObjects = [];

    // Card sized by targetSeats so the user sees their open slots
    // before they fill — 4-seat FFA shows two "Open seat" rows
    // when only the host has joined, which is clearer than rendering
    // a 1-row card that grows over time.
    const cardW = 520;
    const cardLeft = DESIGN_WIDTH / 2 - cardW / 2;
    const cardTop = 455;
    const labelArea = 44;
    const rowH = 52;
    const cardPadBottom = 18;
    const slotCount = Math.max(seats.length, targetSeats);
    const cardH = labelArea + slotCount * rowH + cardPadBottom;

    const cardBg = this.add.graphics();
    cardBg.fillStyle(COLORS.bgPanelInt, 1);
    cardBg.fillRoundedRect(cardLeft, cardTop, cardW, cardH, 8);
    cardBg.lineStyle(1, COLORS.borderSubtleInt, 1);
    cardBg.strokeRoundedRect(cardLeft, cardTop, cardW, cardH, 8);
    this.seatCardObjects.push(cardBg);

    const cardLabel = this.add
      .text(cardLeft + 24, cardTop + 22, 'PLAYERS', {
        fontFamily: FONT_MONO,
        fontSize: '12px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(3)
      .setOrigin(0, 0.5);
    this.seatCardObjects.push(cardLabel);

    // Sort filled seats by seat_index so the order is stable as
    // joiners land. Empty slots fill out the rest of the card.
    const filled = [...seats].sort((a, b) => a.seat_index - b.seat_index);
    const firstRowY = cardTop + labelArea + rowH / 2;
    for (let i = 0; i < slotCount; i++) {
      const rowY = firstRowY + i * rowH;
      const seat = filled[i];
      if (seat) {
        // Held-invite seat: kind='human' AND user_id === null (the
        // server reserves seats this way; AI seats have kind='ai').
        // Render as "INVITED: NAME" + a host-only ✕ to cancel.
        const isHeldInvite =
          seat.kind === 'human' && seat.user_id == null;
        if (isHeldInvite) {
          const label = `${i + 1}.  INVITED: ${seat.name.toUpperCase()}`;
          const text = this.add
            .text(DESIGN_WIDTH / 2, rowY, label, {
              fontFamily: FONT_HEADER,
              fontSize: '20px',
              color: COLORS.textMuted,
              fontStyle: '700',
            })
            .setLetterSpacing(3)
            .setOrigin(0.5);
          this.seatTexts.push(text);

          if (isHost) {
            // ✕ cancel-invite affordance. Pushed to seatTexts so it
            // gets destroyed on next render. Positioned just inside
            // the card's right edge so it doesn't overlap the label.
            const cardRight = cardLeft + cardW;
            const cancelHit = this.add
              .text(cardRight - 22, rowY, '✕', {
                fontFamily: FONT_MONO,
                fontSize: '20px',
                color: COLORS.accentRedHex,
                fontStyle: '700',
              })
              .setOrigin(0.5)
              .setInteractive({ useHandCursor: true });
            cancelHit.on('pointerup', () => {
              void this.handleCancelInvite(seat.seat_index);
            });
            this.seatTexts.push(cancelHit);
          }
        } else {
          const isMe = seat.user_id === myId;
          const aiTag = seat.kind === 'ai' ? '  ·  AI' : '';
          const meTag = isMe ? '  (YOU)' : '';
          const label = `${i + 1}.  ${seat.name.toUpperCase()}${meTag}${aiTag}`;
          const text = this.add
            .text(DESIGN_WIDTH / 2, rowY, label, {
              fontFamily: FONT_HEADER,
              fontSize: '22px',
              color: seat.color,
              fontStyle: '700',
            })
            .setLetterSpacing(3)
            .setOrigin(0.5);
          this.seatTexts.push(text);
        }
      } else {
        // Open slot placeholder — muted text, dotted spacing so the
        // user can see "this is where seat N would go." Reads as
        // "yes, this lobby has room."
        const placeholder = this.add
          .text(DESIGN_WIDTH / 2, rowY, `${i + 1}.  · · ·  OPEN SEAT`, {
            fontFamily: FONT_MONO,
            fontSize: '18px',
            color: COLORS.textMuted,
          })
          .setLetterSpacing(3)
          .setOrigin(0.5);
        this.seatTexts.push(placeholder);
      }
    }

    // Action area at the bottom. As of v0.9.5 there's no manual Start
    // button — the game auto-starts when humanSeats.length reaches
    // targetSeats. The host gets two paths to fill seats: share the
    // public invite link (open to anyone), or send a private invite
    // by username (held seat reserved for one specific user). Non-
    // host joiners only see the Copy link button so they can re-
    // share with friends.
    this.copyButton?.destroy();
    this.copyButton = null;
    this.inviteButton?.destroy();
    this.inviteButton = null;
    this.copyFlashText?.destroy();
    this.copyFlashText = null;

    if (snap.status === 'lobby') {
      if (canInviteMore) {
        this.copyFlashY = 815;
        if (isHost) {
          // Two-button row: Copy link (left) + Invite player (right).
          // Slightly narrower than the single-button layout to fit
          // both inside the design canvas with a comfortable gap.
          this.copyButton = new Button(this, {
            x: DESIGN_WIDTH / 2 - 160,
            y: 875,
            width: 290,
            height: 76,
            text: 'Copy invite link',
            style: 'primary',
            onTap: () => this.copyInviteLink(),
          });
          this.inviteButton = new Button(this, {
            x: DESIGN_WIDTH / 2 + 160,
            y: 875,
            width: 290,
            height: 76,
            text: 'Invite by username',
            style: 'primary',
            onTap: () => this.openInviteModal(),
          });
        } else {
          // Non-host: just the Copy link, full-width as before.
          this.copyButton = new Button(this, {
            x: DESIGN_WIDTH / 2,
            y: 875,
            width: 300,
            height: 76,
            text: 'Copy invite link',
            style: 'primary',
            onTap: () => this.copyInviteLink(),
          });
        }
        if (isHost) {
          const noun = remaining === 1 ? 'player' : 'players';
          const hint = this.add
            .text(
              DESIGN_WIDTH / 2,
              935,
              `Game auto-starts when ${remaining} more ${noun} joins`,
              {
                fontFamily: FONT_MONO,
                fontSize: '14px',
                color: COLORS.textMuted,
              }
            )
            .setLetterSpacing(2)
            .setOrigin(0.5);
          this.hintTexts.push(hint);
        } else {
          const hint = this.add
            .text(
              DESIGN_WIDTH / 2,
              935,
              `Waiting for ${remaining} more — auto-starts on full`,
              {
                fontFamily: FONT_MONO,
                fontSize: '14px',
                color: COLORS.textMuted,
              }
            )
            .setLetterSpacing(2)
            .setOrigin(0.5);
          this.hintTexts.push(hint);
        }
      } else {
        // Lobby is full but the active state hasn't landed yet —
        // realtime will deliver it shortly. Show a calmer hint.
        const hint = this.add
          .text(DESIGN_WIDTH / 2, 875, 'Starting game…', {
            fontFamily: FONT_MONO,
            fontSize: '16px',
            color: COLORS.textMuted,
          })
          .setLetterSpacing(2)
          .setOrigin(0.5);
        this.hintTexts.push(hint);
      }
    }
  }

  private copyInviteLink(): void {
    // Always copy the canonical /game/:id URL, even if the user got
    // here from a longer pasted variant. canonicalOrigin() gives us
    // the right protocol+host on every deploy (preview + prod) and
    // — once Capacitor lands — the public web origin even when the
    // app is running inside a native shell.
    const url = `${canonicalOrigin()}/game/${this.gameId}`;

    const showCopied = (): void => {
      this.copyFlashText?.destroy();
      this.copyFlashText = this.add
        .text(DESIGN_WIDTH / 2, this.copyFlashY, 'Link copied!', {
          fontFamily: FONT_HEADER,
          fontSize: '16px',
          color: COLORS.accentGreenHex,
          fontStyle: '700',
        })
        .setLetterSpacing(3)
        .setOrigin(0.5);
      this.time.delayedCall(1800, () => {
        this.copyFlashText?.destroy();
        this.copyFlashText = null;
      });
    };

    // navigator.clipboard requires HTTPS or localhost. On Vercel both
    // preview and prod are HTTPS so this is the normal path.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(showCopied)
        .catch((err) => {
          console.error('clipboard write failed', err);
          window.prompt('Copy this link to share:', url);
        });
    } else {
      // Fallback for environments without the clipboard API (older
      // browsers, non-secure contexts). Native prompt lets the user
      // copy manually.
      window.prompt('Copy this link to share:', url);
    }
  }

  // --- Invite UI helpers ---

  private openInviteModal(): void {
    if (this.inviteModal) return; // already open
    this.inviteModal = new InviteUsernameModal(this, {
      gameId: this.gameId,
      onCancel: () => {
        this.inviteModal = null;
      },
      onSuccess: (_invitedUsername) => {
        this.inviteModal = null;
        // Realtime UPDATE on game_seats from the server-side insert
        // delivers the new held row, which triggers re-render via the
        // existing subscription. No manual refetch needed.
      },
    });
  }

  private async handleCancelInvite(seatIndex: number): Promise<void> {
    const result = await cancelInvite(this.gameId, seatIndex);
    // 404 means the row is already gone — the user's desired end
    // state (no held invite at this seat) is already true. Most
    // common cause is a double-tap before realtime delivered the
    // first delete. Treat as silent success rather than warn.
    if (!result.ok && result.status !== 404) {
      console.warn('cancel invite failed', {
        gameId: this.gameId,
        seatIndex,
        error: result.error,
        status: result.status,
      });
    }
    // Refresh regardless of outcome to heal any stale UI.
    void this.refreshSnapshot();
  }

  // --- Launch ---

  private launchGame(): void {
    if (this.transitioning) return;
    this.transitioning = true;
    const snap = this.snapshot;
    if (!snap) return;
    const store = createRemoteGameStore(snap);
    this.channelCleanup?.();
    this.scene.start('Game', { remoteStore: store });
  }
}
